import { Suspense } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { BrandMark } from '../components/ui';
import { AccountNav } from '../components/layout/AccountNav';
import { UserMenu } from '../components/layout/UserMenu';
import { Footer } from '../components/Footer';
import { ChatButton } from '../components/ChatButton';
import { RouteErrorBoundary } from '../components/ErrorBoundary';
import { PageLoader } from '../components/SuspenseFallback';
import { SkipToContent } from '../utils/accessibility';
import authService from '../lib/auth';

/**
 * Shell for `/akun/*` (formerly CustomerLayout).
 *
 * No CommandPalette and no ThemeToggle: Ctrl+K was a leftover from the internal
 * dashboard this product stopped being, and dark mode is out for launch
 * (CONTRACT.md §2.7). The user chip shows the signed-in person's real name —
 * there is no "Admin User" fallback, because a name nobody owns is a lie.
 *
 * One header row on desktop; on mobile the nav wraps to its own line and scrolls
 * inside itself, so four items never widen <body> at 375px. The wrap is done
 * with flex `order` rather than by rendering the chip twice.
 */

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function AccountLayout() {
  const user = authService.getUser();

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <SkipToContent />
      <header className="border-b-2 border-surface-2">
        <div className="mx-auto flex max-w-[1180px] flex-wrap items-center gap-x-[34px] gap-y-1 px-5 py-2 md:h-[74px] md:flex-nowrap md:px-16 md:py-0">
          <Link
            to="/"
            className="order-1 mr-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink md:mr-0"
            aria-label="Jualan, Lapak Bang Ade. Ke beranda"
          >
            <BrandMark />
          </Link>

          {user && (
            <UserMenu
              align="right"
              className="order-2 md:order-3"
              triggerClassName="rounded-pill"
              trigger={
                <span className="flex items-center gap-3 rounded-pill border-2 border-muted py-[5px] pl-4 pr-[5px]">
                  <span className="text-[14.5px] font-extrabold text-ink">{user.name}</span>
                  <span
                    aria-hidden="true"
                    className="flex h-8 w-8 items-center justify-center rounded-pill bg-accent text-[13px] font-extrabold text-ink"
                  >
                    {initialsOf(user.name)}
                  </span>
                </span>
              }
            />
          )}

          <AccountNav className="order-3 -mx-5 w-full overflow-x-auto px-5 md:order-2 md:mx-0 md:mr-auto md:w-auto md:overflow-visible md:px-0" />
        </div>
      </header>

      <main id="main-content" className="flex-1">
        <RouteErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </RouteErrorBoundary>
      </main>

      <Footer />
      <ChatButton />
    </div>
  );
}

export default AccountLayout;
